"use client";

import { FixedLayout } from "@/components/shared/fixed-layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Controller } from "react-hook-form";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { useSmsVerification } from "@/lib/hooks/use-sms-verification";
import { useSignupStore } from "@/lib/store/use-signup-store";

const PERSONAL_SMS_VERIFICATION_TITLE = `본인의 휴대전화 번호로
SMS 인증을 진행해 주세요.`;

const PLACEHOLDER = {
  PHONE_NUMBER: "01012345678",
  VERIFICATION_CODE: "인증번호 6자리를 입력해 주세요.",
};

export function PersonalSmsStep() {
  const { goToPrevStep } = useSignupStore();
  const {
    control,
    handleSubmit,
    isValid,
    trigger,
    isVerificationSent,
    remainingTime,
    cooldownTime,
    isSending,
    isVerifying,
    isSigningUp,
    handleSendVerification,
    handlePhoneNumberChange,
    onSubmit,
    formatTime,
  } = useSmsVerification();

  return (
    <FixedLayout
      title={PERSONAL_SMS_VERIFICATION_TITLE}
      showButton={isVerificationSent}
      buttonText="인증번호 확인"
      buttonDisabled={
        !isValid || remainingTime === 0 || isVerifying || isSigningUp
      }
      onSubmit={handleSubmit(onSubmit)}
      onBack={goToPrevStep}
    >
      <form id="sms-verification-form" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-6">
          {/* 휴대전화 번호 입력 */}
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field, fieldState }) => (
              <Field>
                <FieldLabel htmlFor="personal-sms-phone-number">
                  휴대전화 번호
                </FieldLabel>
                <div className="w-full flex gap-2">
                  <Input
                    {...field}
                    id="personal-sms-phone-number"
                    type="tel"
                    placeholder={PLACEHOLDER.PHONE_NUMBER}
                    aria-invalid={fieldState.invalid}
                    onChange={(e) =>
                      handlePhoneNumberChange(e.target.value, field.onChange)
                    }
                    wrapperClassName="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={async () => {
                      const isPhoneValid = await trigger("phoneNumber");
                      if (isPhoneValid) {
                        await handleSendVerification();
                      }
                    }}
                    disabled={
                      !field.value ||
                      fieldState.invalid ||
                      isSending ||
                      cooldownTime > 0
                    }
                  >
                    {cooldownTime > 0
                      ? `${cooldownTime}초 후 재발송`
                      : isVerificationSent
                        ? "인증번호 재발송"
                        : "인증번호 받기"}
                  </Button>
                </div>
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />

          {/* 인증번호 입력 */}
          {isVerificationSent && (
            <Controller
              name="verificationCode"
              control={control}
              render={({ field, fieldState }) => (
                <Field>
                  <div className="flex justify-between items-center">
                    <FieldLabel htmlFor="personal-sms-verification-code">
                      인증번호
                    </FieldLabel>
                    <span
                      className={`text-sm ${
                        remainingTime > 0 ? "text-gray-600" : "text-red-500"
                      }`}
                    >
                      {remainingTime > 0
                        ? formatTime(remainingTime)
                        : "시간 만료"}
                    </span>
                  </div>
                  <Input
                    {...field}
                    id="personal-sms-verification-code"
                    type="text"
                    inputMode="numeric"
                    placeholder={PLACEHOLDER.VERIFICATION_CODE}
                    aria-invalid={fieldState.invalid}
                    maxLength={6}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, "");
                      field.onChange(value);
                    }}
                  />
                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              )}
            />
          )}
        </div>
      </form>
    </FixedLayout>
  );
}
